<template>
  <div class="ReportHeader my-3">
    <v-row class="headerCaption" align-start justify="space-between">
      <v-col cols="12" md="8" class="pa-0 py-2 text-center text-md-left fill-height overflow-hidden">
        <span class="headline">{{$t("Header.performanceReport", [orgName])}}</span>
      </v-col>
      <v-col cols="12" md="4" class="pa-0 py-2 text-center text-md-right no-wrap d-flex">
        <OrganizationFilter
          v-if="(userRoles && userRoles.isAdmin) || allowedOrganizations.length > 0"
          :allowedOrgs="allowedOrganizations"
          :selectedOrganization="selectedOrganization"
          :solo="solo"
          :outline="outline"
        />
        <v-select
          v-model="selectedDate"
          :items="optionsDates"
          item-text="label"
          item-value="id"
          solo
          height="40px"
          append-icon=""
          single-line
          dense
          flat
          class="DateSelector ma-0 ml-3"
          color="black"
          :loading="loadingQuery && '#0098E1'"
          @click="openDateSelector"
          hide-details
        >
          <template v-slot:append>
            <v-icon color="black">date_range</v-icon>
          </template>
          <template
            v-for="slotName in ['item', 'selection']"
            :slot="slotName"
            slot-scope="data"
          >
            <div slot="item" :class="isCampaignReport(data.item) ? 'ReportDateItem CampaignReportDate' : 'ReportDateItem'">
              <v-icon v-if="data.item.isPublished === false" small color="warning">priority_high</v-icon>
              <span>{{data.item.label}}</span>
            </div>
          </template>
        </v-select>
      </v-col>
    </v-row>
    <v-divider />
    <v-row v-if="cardsData" row wrap class="headerCards mt-3 mb-8" justify-center>
      <v-col xs12 sm4 class="pa-0 pr-4 BlueGradient">
        <v-card dark class="rounded-sm py-4 px-2" >
          <h4 class="text-center body-2 pb-3 text-truncate">{{$t("Header.totalIrrigationHours")}}</h4>
          <div class="d-flex justify-center align-center my-2">
            <span class="headline">{{cardsData.totalHours.toFixed(1).replace(/\.0+$/, '')}}</span>
            <span class="subheading">hs</span>
          </div>
        </v-card>
      </v-col>
      <v-col xs12 sm4 class="pa-0 pr-4" :class="cardsData.pressureState ? 'GreenGradient' : 'RedGradient'">
        <v-card dark class="rounded-sm py-4 px-2" >
          <h4 class="text-center body-2 pb-3 text-truncate">{{$t("Header.workingPressure")}}</h4>
          <div class="d-flex justify-center align-center my-2">
            <template v-if="cardsData.pressureState">
              <v-icon large class="mr-2">check</v-icon>
              <span class="headline">{{$t("Header.correct")}}</span>
            </template>
            <template v-else>
              <v-icon large class="mr-2">warning</v-icon>
              <span class="headline">{{$t("Header.incorrect")}}</span>
            </template>
          </div>
        </v-card>
      </v-col>
      <v-col xs12 sm4 class="pa-0" :class="cardsData.totalFaults ? 'RedGradient' : 'GreenGradient'">
        <v-card dark class="rounded-sm py-4 px-2" >
          <h4 class="text-center body-2 pb-3 text-truncate">{{$t("Header.faultsInDevice")}}</h4>
          <div class="d-flex justify-center align-center my-2">
            <v-icon large class="mr-2">warning</v-icon>
            <span class="headline">{{cardsData.totalFaults}} {{$t("Header.faults")}}</span>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import OrganizationFilter from '../Dashboard/OrganizationFilter'
import { trackEvent  } from '../../lib/track-events'
import { EVENTS_TO_TRACK } from '../../constants/const'

const dateToLocaleArray = (date) => {
  const auxLocaleOptions = { year: 'numeric', month: 'short', day: 'numeric' }
  let resultArray = new Date(date + ' 00:00').toLocaleDateString('es-ES', auxLocaleOptions)
  resultArray = resultArray.split(' ')
  resultArray[1] = resultArray[1].charAt(0).toUpperCase() + resultArray[1].slice(1)

  return resultArray
}

const parseDateLabels = (report) => {
  let newLabel = ''
  let auxFrom = dateToLocaleArray(report.from_date)
  newLabel += `${auxFrom[1]} ${auxFrom[0]} - `

  let auxTo = dateToLocaleArray(report.to_date)
  newLabel += `${auxTo[1]} ${auxTo[0]}, ${auxTo[2]}`

  return newLabel
}

export default {
  data () {
    const initialDate = (this.reportDates && this.reportDates[0] && this.reportDates[0].id) || 0
    if (initialDate) {
      this.$emit('changeReport', initialDate)
    }

    return {
      selectedDate: initialDate,
      solo: true,
      outline: false,
      userRoles: this.$store.getters.userRoles,
      selectedOrganization: this.$store.getters.selectedOrganization.id
    }
  },
  components: {
    OrganizationFilter
  },
  props: {
    reportDates: Array,
    orgInfo: Object,
    cardsData: Object,
    loadingQuery: Boolean,
    date: String
  },
  computed: {
    optionsDates () {
      if (!this.reportDates.length) {
        return [{id: 0, label: this.$t('Header.noReports')}]
      } else {
        return this.reportDates.map((report) => {
          return {
            id: report.id,
            label: parseDateLabels(report),
            isPublished: report.is_published,
            fromDate: report.from_date,
            toDate: report.to_date
          }
        })
      }
    },
    orgName () {
      return this.$store.getters.selectedOrganization.name || (this.orgInfo && this.orgInfo.name)
    },
    allowedOrganizations () {
      if (this.userRoles && (this.userRoles.isAdmin || this.userRoles.farmhand)) {
        return []
      }

      const orgs = Object.keys(this.$store.getters.userPermissions)
      if (orgs.length > 0) {
        orgs.push(this.$store.getters.userOrganization.id)
      }
      return orgs
    }
  },
  methods: {
    findDate (dates) {
      return dates.find((e) => {
        return new Date(this.date) >= new Date(e.from_date) && new Date(this.date) <= new Date(e.to_date)
      })
    },
    isCampaignReport (date) {
      const CAMPAIGN_REPORT_MIN_DAYS = 45
      const diffTime = new Date(date.toDate).getTime() - new Date(date.fromDate).getTime()

      // If the report has a date difference greater than 45 days, it is considered a campaign report
      return (diffTime / 1000 / 60 / 60 / 24) > CAMPAIGN_REPORT_MIN_DAYS
    },
    openDateSelector () {
      trackEvent(EVENTS_TO_TRACK.CLICK_REPORTS_DATE_SELECTOR)
    }
  },
  watch: {
    reportDates (newVal) {
      if (!newVal.length) {
        this.selectedDate = 0
        return
      }

      // Get report date object that contains the requested date from the URL.
      const selectedDate = this.findDate(newVal)
      if (selectedDate) {
        this.selectedDate = selectedDate.id
      } else {
        this.selectedDate = newVal[0].id || 0
      }

      // If date was entered by url and was not found, show an alert
      if (!selectedDate && this.date) {
        this.$parent.alertURLReportNotFound()
      }
    },
    selectedDate () {
      trackEvent(EVENTS_TO_TRACK.OPEN_FULL_REPORT, { reportId: this.selectedDate })
      this.$emit('changeReport', this.selectedDate)
    }
  }
}
</script>


// WEBPACK FOOTER //
// Header.vue?3c5f708a